// Third Party
import * as React from 'react';
import debounce from 'lodash/debounce';

// Utils
import { extractFirstPhraseBeforeComma } from '../../utils/extract-first-phrase-before-comma';
import { usePlacesAutocompleteLazyQuery, PlaceTypeFilter } from '@gql';

// UI
import { SearchSelectAutocomplete } from '@components/form/select-autocomplete/search-select-autocomplete';
import { IDropdownOption } from '@components/dropdown-option-list';

export enum PlacesSelectAutocompleteFilters {
  ALL = 'ALL',
  ADDRESS = 'ADDRESS',
  ESTABLISHMENT = 'ESTABLISHMENT',
  GEOCODE = 'GEOCODE',
  REGIONS = 'REGIONS',
}

export type IPlacesSelectAutocompleteOption = IDropdownOption<unknown>;
export interface IPlacesSelectAutocompleteProps {
  /** Custom content to place in the left side */
  leftContent?: React.ReactNode;
  /* Indicate if the label text */
  label?: string;
  /* Indicate if the field is invalid */
  isInvalid?: boolean;
  /* Indicate if the field has a border */
  hasBorder?: boolean;
  /** Custom content to place in the right side */
  rightContent?: React.ReactNode;
  /** Define the name of the input element */
  name: string;
  /** Defines the label to be displayed when there are no results */
  noResultsLabel: string;
  /** Define the placeholder of the input element */
  placeholder: string;
  /* Indicate if the label should be visible */
  showLabel?: boolean;
  /** Define if label is left aligned */
  labelLeftAligned?: boolean;
  /** Function to be executed when changing the selected value */
  onChange?: (
    name: string,
    value: IPlacesSelectAutocompleteOption | null
  ) => void;
  /** Filter places by type */
  placesFilter?: PlacesSelectAutocompleteFilters;
  initialValue?: IPlacesSelectAutocompleteOption | null;
}

export const PlacesSelectAutocomplete: React.FC<
  IPlacesSelectAutocompleteProps
> = ({
  label,
  name,
  placeholder,
  noResultsLabel,
  leftContent,
  rightContent,
  showLabel,
  onChange,
  labelLeftAligned = false,
  isInvalid = false,
  hasBorder = true,
  placesFilter = PlacesSelectAutocompleteFilters.ALL,
  initialValue = null,
}) => {
  const [options, setOptions] = React.useState<
    IPlacesSelectAutocompleteOption[]
  >([]);
  const [value, setValue] =
    React.useState<IPlacesSelectAutocompleteOption | null>(initialValue);
  const [placesAutocompleteQuery, { data }] = usePlacesAutocompleteLazyQuery();

  const onSelect = (
    name: string,
    value: IPlacesSelectAutocompleteOption | null
  ) => {
    setValue({
      label: extractFirstPhraseBeforeComma(value?.label || ''),
      leftContent: value?.leftContent,
      value: value?.value || '',
    });
    onChange?.(name, value);
  };

  const debouncedonInputChange = debounce(async (value: string) => {
    void placesAutocompleteQuery({
      variables: {
        query: value,
        type: filterMapper(placesFilter),
      },
    });
  }, 225);

  const onInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value) {
        debouncedonInputChange(event.target.value);
      }
    },
    []
  );

  React.useEffect(() => {
    if (data?.placesAutocomplete) {
      setOptions(
        data.placesAutocomplete.map((place) => ({
          label: place.name,
          value: place.id,
        }))
      );
    }
  }, [data, value]);

  return (
    <SearchSelectAutocomplete
      isInvalid={isInvalid}
      hasBorder={hasBorder}
      label={extractFirstPhraseBeforeComma(label || '')}
      showLabel={showLabel}
      leftContent={leftContent}
      rightContent={rightContent}
      name={name}
      noResultsLabel={data ? noResultsLabel : 'Loading...'}
      options={options}
      placeholder={placeholder}
      value={value}
      onChange={onSelect}
      onInputChange={onInputChange}
      labelLeftAligned={labelLeftAligned}
    />
  );
};

const filterMapper = (
  filter: PlacesSelectAutocompleteFilters
): PlaceTypeFilter => {
  switch (filter) {
    case PlacesSelectAutocompleteFilters.ALL:
      return PlaceTypeFilter.All;
    case PlacesSelectAutocompleteFilters.ADDRESS:
      return PlaceTypeFilter.Address;
    case PlacesSelectAutocompleteFilters.ESTABLISHMENT:
      return PlaceTypeFilter.Establishment;
    case PlacesSelectAutocompleteFilters.GEOCODE:
      return PlaceTypeFilter.Geocode;
    case PlacesSelectAutocompleteFilters.REGIONS:
      return PlaceTypeFilter.Regions;
  }
};
